/**
 * How busy the browser is with API traffic right now.
 *
 * WHY THIS EXISTS
 * ---------------
 * A reload of /payments took up to 8s. The trace (`download.devtools`) shows the
 * page's own requests were not the problem: the document came back in 15-3000ms,
 * the payments chunk batch in ~100ms, and `/api/payments` answered straight away.
 * What filled the rest of the timeline were BACKGROUND PREFETCHES - the idle
 * route warm-up in routes/prefetch.js fired four waves of chunks at +3.5s, +4.0s,
 * +4.9s, +5.8s and +7.7s, each competing with the current page's chunks and API
 * calls on the one connection the app keeps open.
 *
 * `requestIdleCallback` is the wrong signal on a slow link: the main thread is
 * idle precisely because it is *waiting on the wire*. The right signal is "no API
 * request in flight, and none finished in the last N ms", which is what this
 * module tracks. `api.js` reports every request here; `prefetch.js` waits for
 * quiet before spending bandwidth on speculative downloads.
 */

let inflight = 0;
let lastActivityAt = Date.now();

/** Call when a request leaves the browser. */
export function beginNetworkActivity() {
  inflight += 1;
  lastActivityAt = Date.now();
}

/** Call when a request settles (success, error or abort). */
export function endNetworkActivity() {
  inflight = Math.max(0, inflight - 1);
  lastActivityAt = Date.now();
}

export function getInflightRequestCount() {
  return inflight;
}

export function getLastNetworkActivityAt() {
  return lastActivityAt;
}

const DEFAULT_QUIET_MS = 1500;
const DEFAULT_TIMEOUT_MS = 20000;
const POLL_MS = 250;

/**
 * Resolve once no request is in flight AND nothing has been requested for
 * `quietMs`. Resolves anyway after `timeoutMs`, so a caller can never hang: the
 * work being deferred is best-effort by definition.
 */
export function whenNetworkIdle(options = {}) {
  const quietMs = Number.isFinite(Number(options.quietMs)) ? Number(options.quietMs) : DEFAULT_QUIET_MS;
  const timeoutMs = Number.isFinite(Number(options.timeoutMs)) ? Number(options.timeoutMs) : DEFAULT_TIMEOUT_MS;

  return new Promise((resolve) => {
    if (typeof setTimeout !== 'function') {
      resolve(false);
      return;
    }
    const startedAt = Date.now();
    const finish = (idle) => resolve(idle);

    const tick = () => {
      const now = Date.now();
      const quietFor = now - lastActivityAt;
      if (inflight === 0 && quietFor >= quietMs) {
        finish(true);
        return;
      }
      if (now - startedAt >= timeoutMs) {
        finish(false);
        return;
      }
      const untilQuiet = inflight === 0 ? Math.max(POLL_MS, quietMs - quietFor) : POLL_MS;
      setTimeout(tick, Math.min(untilQuiet, Math.max(POLL_MS, timeoutMs - (now - startedAt))));
    };

    tick();
  });
}

/** Test helper: forget accumulated traffic. */
export function __resetNetworkActivityForTests() {
  inflight = 0;
  lastActivityAt = Date.now();
}
